import { z } from "zod";
import { shoppingCardSpec } from "./shoppingCatalog.ts";
import {
  connectorIconVariant,
  headerIcon,
  reminderOverrideSchema,
  virtualMeetingType,
} from "./artifacts-shared.ts";

/**
 * Schema-driven artifact view payload. Each heavy artifact response can carry
 * an optional `view` describing its UI as an ordered list of typed
 * components. A generic web/native renderer dispatches on `component.type`;
 * a generic PATCH /a/.../artifacts/:id endpoint applies field-level edits
 * using the dot-paths declared by editable components. Per-artifact-type
 * code is reduced to a `buildView()` + `applyFieldPatches()` pair on the
 * server.
 *
 * Vocabulary:
 *   - `type` discriminates which renderer to use ("text",
 *     "dateTimeRange", "horizontalGroup", ...).
 *   - `id` is a stable identifier within a single view, used as the React
 *     key so child state survives re-renders.
 *   - `path` is the dot-path the component reads from / writes to in the
 *     typed artifact data. Layout-only components (`divider`,
 *     `horizontalGroup`, `infoBanner`, etc.) have no path.
 */

const baseComponent = z.object({
  /** Stable id, unique within the view — used as React key. */
  id: z.string(),
  /** When true, the component renders edge-to-edge: `ArtifactView` cancels its
   *  own horizontal padding (and the top padding when the component is first)
   *  so the element spans the full card width. Used for banner strips and
   *  other full-width treatments; any component type can opt in. */
  fullBleed: z.boolean().optional(),
});

export const textComponent = baseComponent.extend({
  type: z.literal("text"),
  /** Dot-path the input is bound to (e.g. "summary", "emailSubject"). Only
   * required when `editable: true`; read-only text components can omit it. */
  editPath: z.string().optional(),
  label: z.string().optional(),
  value: z.string(),
  editable: z.boolean(),
  /** Optional leading icon rendered before the text (e.g. an external-link
   *  glyph on a "Connect <provider>" title). */
  icon: headerIcon.optional(),
  /** `"plain"` => bare inline text input. `"headline"` => heading-styled
   * rich-text editor (invitation titles, document titles — values that may
   * carry inline formatting). `"title"` => heading-styled single-line input
   * (plain names like a routine name — no rich text). A non-editable
   * `"headline"`/`"title"` renders as static heading text rather than
   * mounting an editor. */
  variant: z.enum(["plain", "headline", "title"]).default("plain"),
  /** Render as a bordered form input (label above, boxed field) rather than a
   *  bare inline input. Use for form-style cards like the routine editor. */
  boxed: z.boolean().optional(),
});

export const markdownComponent = baseComponent.extend({
  type: z.literal("markdown"),
  editPath: z.string(),
  /** Markdown source. What an editor seeds from, and what a save writes back. */
  value: z.string(),
  /**
   * What to show when the body is only being read, where that differs from
   * `value` — today, a Slack draft whose `<@U…>` mentions render as names.
   *
   * Read-only by construction: an editor must still seed from `value`, because
   * the stored body is the wire form and the rendering is lossy. A client that
   * does not know this field renders `value`, which is what it did before.
   */
  displayValue: z.string().optional(),
  editable: z.boolean(),
  hideToolbar: z.boolean().optional(),
  showDebug: z.boolean().optional(),
  /** Optional field label rendered above the editor (e.g. "About"). */
  label: z.string().optional(),
  /** Optional muted helper line rendered under the label. */
  helpText: z.string().optional(),
  /** Render the editor inside a bordered form box. Use for form-style cards
   *  like the routine editor so the field reads as editable. */
  boxed: z.boolean().optional(),
  /**
   * Where each embedded file image is served for this reader, by file artifact
   * id. Set only on a shared reader's view, whose session cannot reach the
   * owner's routes; the paths authorize the share themselves, so clients use
   * them as given.
   */
  imageSrcs: z.record(z.string(), z.string()).optional(),
});

/** A flat participant shape that absorbs both EmailDraftParticipant and
 * EventParticipant. Server-side mappers fill the union of fields; the renderer
 * picks what it needs. */
/** Permissive participant shape that accepts every participant kind we have
 * (event, emailDraft, kickoff, phone, textMessage, directMessage). Most
 * fields are optional; the renderer picks what it needs. Keeping a single
 * flat type — rather than a union — is what lets `participantList` work
 * uniformly across artifact types without each renderer narrowing per
 * union arm. */
const viewParticipant = z.object({
  id: z.string(),
  name: z.string(),
  value: z.string(),
  picture: z.string().optional(),
  type: z.enum(["emailAddress", "phoneNumber", "address"]).optional(),
  urn: z.string().optional(),
  contactId: z.string().optional(),
  state: z.enum(["attending", "tentative", "declined", "unknown"]).optional(),
  /** This participant is the user. Set on outgoing invitations. */
  isSelf: z.boolean().optional(),
  /** The provider-reported organizer. Only set on received invitations. */
  organizer: z.boolean().optional(),
  required: z.boolean().optional(),
  isDomainHandle: z.boolean().optional(),
  optional: z.boolean().optional(),
});
export type ViewParticipant = z.infer<typeof viewParticipant>;

export const participantListComponent = baseComponent.extend({
  type: z.literal("participantList"),
  /** Display label ("To:", "Attendees", etc.). When `role` is set the
   * renderer derives the visible prefix from that instead. */
  label: z.string().optional(),
  /** Optional icon for the label header in modal mode (e.g. userPlus/userMinus
   * for added/removed attendees). Defaults to a people glyph. */
  icon: headerIcon.optional(),
  /** Email-recipient role. When set, the component renders with the matching
   * "To:" / "Cc:" / "Bcc:" / "From:" label and styling. Used by emailDraft
   * to express its three editable recipient lists as separate components. */
  role: z.enum(["to", "cc", "bcc", "from"]).optional(),
  /** `"multi"` (default — pill list with add/remove) or `"single"` — exactly
   * one recipient (no add affordance). Used by directMessageDraft,
   * textMessageDraft, phoneCallDraft. Optional in the wire format; the
   * renderer treats undefined as `"multi"`. */
  cardinality: z.enum(["multi", "single"]).optional(),
  /** Path for the address-id list this component writes back to. e.g.
   * "participantAddressIds" (invitation) or "participants" (kickoff — server
   * adapts to the per-type write shape). */
  editPath: z.string().optional(),
  /** Provides handle-switching popover (kickoff). */
  allowChangeHandle: z.boolean().optional(),
  /** Provides Optional/Required toggle (kickoff). */
  allowToggleOptional: z.boolean().optional(),
  /** Inline edit (kickoff) vs. pencil-button modal edit (invitation). */
  editMode: z.enum(["inline", "modal"]).default("inline"),
  participants: z.array(viewParticipant),
  editable: z.boolean(),
  /** Filter out participants by handle value (e.g. assistant emails). */
  hideValues: z.array(z.string()).optional(),
  /** `"handle"` (default — treats `participants[].id` as a Handle id and the
   * picker resolves contacts; the write payload is the address/handle id
   * list, or kickoff-shaped objects for inline mode) vs. `"email"` (bare
   * email-string filters: pills carry `id === value === email`, the picker
   * accepts free-form email entry, and the write payload is `string[]`).
   * Used by the routine-config form for fromSenders/toRecipients/ccRecipients
   * and calendar organizer/attendee filters. Optional on the wire; the
   * renderer treats undefined as `"handle"`. */
  participantSource: z.enum(["handle", "email"]).optional(),
});

export const dateTimeRangeComponent = baseComponent.extend({
  type: z.literal("dateTimeRange"),
  startEditPath: z.string(),
  endEditPath: z.string(),
  startMs: z.number().optional(),
  endMs: z.number().optional(),
  htmlLink: z.string().optional(),
  /** Server-formatted range string, already converted to the account's
   *  effective IANA zone (via `getEffectiveTimezone`). The client renders this
   *  verbatim for the read view so the displayed time matches the account
   *  timezone regardless of device (BIG-4825). The `ms` fields drive the edit
   *  picker only. */
  displayText: z.string().optional(),
  editable: z.boolean(),
});

export const recurrenceComponent = baseComponent.extend({
  type: z.literal("recurrence"),
  editPath: z.string(),
  rrule: z.array(z.string()).optional(),
  description: z.string().optional(),
  /** Anchor date for the picker; ms epoch. */
  anchorMs: z.number().optional(),
  editable: z.boolean(),
});

export const remindersComponent = baseComponent.extend({
  type: z.literal("reminders"),
  editPath: z.string(),
  value: z.array(reminderOverrideSchema).optional(),
  editable: z.boolean(),
});

export const locationComponent = baseComponent.extend({
  type: z.literal("location"),
  editPath: z.string(),
  value: z.string(),
  editable: z.boolean(),
});

export const virtualMeetingComponent = baseComponent.extend({
  type: z.literal("virtualMeeting"),
  /** Renamed from `type` (the meeting-platform field) to avoid colliding
   * with the discriminator above. */
  platform: virtualMeetingType,
  url: z.string().optional(),
});

export const infoBannerComponent = baseComponent.extend({
  type: z.literal("infoBanner"),
  severity: z.enum(["info", "warning", "error"]).default("info"),
  icon: z.enum(["info", "zap", "alertTriangle", "sparkle"]).optional(),
  title: z.string(),
  /** Optional. When omitted, renders as a single-line title-only strip — used
   * by emailDraft's external-recipient warning to match the legacy style. */
  body: z.string().optional(),
});

/** Bare section heading inserted between components (e.g. "Notes" before the
 * description on an invitation). No data binding. The optional `icon` renders
 * to the left of the text — used by the routine-config form for the
 * "When / Then do / Permissions" section headers. */
export const sectionLabelComponent = baseComponent.extend({
  type: z.literal("sectionLabel"),
  text: z.string(),
  icon: headerIcon.optional(),
  /** `"normal"` (default — base/foreground heading) matches existing
   * invitation "Notes" usage; `"caption"` (smaller, uppercase, muted)
   * matches the routine-form section headers. Optional on the wire; renderer
   * treats undefined as `"normal"`. */
  variant: z.enum(["normal", "caption"]).optional(),
});

/** Horizontal rule between components. `spacing` controls vertical margin —
 * `tight` matches the kickoff between-section breaks (`my-2`), `normal`
 * matches the larger section break (`my-4`). */
export const dividerComponent = baseComponent.extend({
  type: z.literal("divider"),
  spacing: z.enum(["tight", "normal"]).default("normal"),
});

export const toggleComponent = baseComponent.extend({
  type: z.literal("toggle"),
  editPath: z.string(),
  label: z.string(),
  helpText: z.string().optional(),
  /** Inline-style pill labels; default to Off/On in the renderer. */
  leftLabel: z.string().optional(),
  rightLabel: z.string().optional(),
  value: z.boolean(),
  editable: z.boolean(),
  /** `"inline"` (default) => compact label + info-tooltip + Off/On pill switch
   *  (fits in a horizontal group). `"card"` => full-width bordered row with the
   *  help text shown inline beneath the label and a switch on the right (the
   *  legacy permission-toggle look). */
  style: z.enum(["inline", "card"]).optional(),
});

/** Compact colored pill — icon + label. Used for connection status,
 * RSVP responses, and any other "this thing is in state X" indicator. */
export const statusBadgeComponent = baseComponent.extend({
  type: z.literal("statusBadge"),
  label: z.string(),
  icon: headerIcon.optional(),
  /** Drives the renderer's color palette (info=blue, success=green,
   * warning=amber, error=red, muted=gray). */
  severity: z.enum(["info", "success", "warning", "error", "muted"]).default("info"),
});

/** A single "label: value" row, optionally with a leading icon. The renderer
 * formats `value` according to `valueType` — e.g. phoneNumber gets E.164
 * formatting, location wraps in the in-app LocationDisplay. */
export const fieldRowComponent = baseComponent
  .extend({
    type: z.literal("fieldRow"),
    icon: headerIcon.optional(),
    label: z.string().optional(),
    value: z.string(),
    /** `badge` renders the value as an outline pill (e.g. a calendar name).
     *  `url` renders the value as an external link opening in a new tab. */
    valueType: z.enum(["text", "location", "phoneNumber", "email", "badge", "url"]).default("text"),
    /** Render the label like a recipient-row label (bold, fixed width) so the
     *  value aligns with adjacent participant rows (e.g. phone-call "Type:"). */
    prominent: z.boolean().optional(),
    /** When set, the value is rendered as a link that opens the calendar
     *  day-view (and pre-highlights the time range). The string `value` is the
     *  display text; these ms are what the link navigates to. */
    calendarLink: z
      .object({
        startDateMs: z.number(),
        endDateMs: z.number().optional(),
        isAllDay: z.boolean().optional(),
      })
      .optional(),
    /** When set, the value is rendered as an in-app link to this path (wouter
     *  navigation), e.g. `/communications/:id` for a replied-to source email. */
    appLink: z.string().optional(),
  })
  .refine((row) => !(row.valueType === "url" && (row.appLink || row.calendarLink)), {
    // A `url` value renders its own `<a>`, while appLink/calendarLink wrap the
    // value in their own link element. Combining them would nest anchors
    // (`<a><a>`) or an anchor inside a button — invalid interactive nesting.
    error: "fieldRow with valueType 'url' cannot also set appLink or calendarLink",
    path: ["valueType"],
  });

/** Before/after comparison row — icon + label + (optional oldValue → )
 * newValue. Used for event-change diffs (subject, location, time, etc.). */
export const diffComponent = baseComponent.extend({
  type: z.literal("diff"),
  icon: headerIcon.optional(),
  label: z.string(),
  oldValue: z.string().optional(),
  newValue: z.string(),
  valueType: z.enum(["text", "location"]).default("text"),
});

/** Inline image preview. `src` is an authenticated URL the renderer fetches
 * with the user's session (use for first-party assets only). */
export const imagePreviewComponent = baseComponent.extend({
  type: z.literal("imagePreview"),
  src: z.string(),
  /** Optional higher-resolution source used when the card is maximized; the
   *  renderer falls back to `src` inline to avoid over-fetching. */
  srcLarge: z.string().optional(),
  alt: z.string(),
  /** When true, the URL needs the user's session — the renderer omits it on
   *  shared (unauthenticated) threads where the fetch would 401. */
  requiresAuth: z.boolean().optional(),
  /** Owner-only images are removed from public snapshot payloads entirely. */
  sharedVisibility: z.enum(["include", "omit"]).optional(),
});

/** Inline audio player. `src` is an authenticated URL the renderer streams with
 * the user's session (use for first-party assets only). Used for phone-call
 * recordings, where hearing the call is what makes a transcript checkable.
 *
 * `heading` rides on the component rather than being emitted as a sibling
 * `sectionLabel` so a renderer without audio support drops the heading along
 * with the player instead of leaving a title over nothing. */
export const audioPlayerComponent = baseComponent.extend({
  type: z.literal("audioPlayer"),
  src: z.string(),
  /** Section title rendered above the player, e.g. "Recording". */
  heading: z.string().optional(),
  /** Accessible description of the audio, e.g. "Recording of call with Ana". */
  label: z.string(),
  /** When true, the URL needs the user's session — the renderer omits it on
   *  shared (unauthenticated) threads where the fetch would 401. */
  requiresAuth: z.boolean().optional(),
});

/**
 * Headline for a connect card: the service's own mark beside its name.
 *
 * The mark can't come from `headerIcon` — that enum is our own glyph set, and
 * these are third-party brands. The renderer resolves it from its provider
 * icon maps when the service has a hand-drawn SVG (Google, Slack, Zoom, …),
 * and falls back to a favicon fetched from `websiteUrl` for the long tail of
 * catalog connectors (every MCP server, every OpenAPI spec) that doesn't.
 */
export const connectorHeaderComponent = baseComponent.extend({
  type: z.literal("connectorHeader"),
  /** Headline text, server-authored (e.g. "Connect Google Drive"). */
  title: z.string(),
  /** Connector family — the key into the renderer's brand icon maps. */
  provider: z.string(),
  /**
   * Which mark to draw for a provider fronting several products: connecting
   * Google as a calendar source should show the Calendar mark, as a mailbox
   * the Gmail mark, as a login the plain Google mark.
   */
  iconVariant: connectorIconVariant.default("brand"),
  /** Favicon source for connectors with no hand-drawn mark. */
  websiteUrl: z.string().optional(),
});

/**
 * Per-kind framing for {@link pickerComponent}. Each member carries only what
 * the renderer can't work out for itself — which things to consider, in what
 * order — never the things themselves.
 */
const connectorsPickerTarget = z.object({
  kind: z.literal("connectors"),
  /** Restrict the list to these catalog ids; empty means the whole catalog. */
  scopeIds: z.array(z.string()).default([]),
  /**
   * Catalog ids pinned above the rest, in this order, with a separator under
   * them. Empty means no pinning and no separator.
   */
  pinnedIds: z.array(z.string()).default([]),
  /**
   * Catalog ids in the order to show the unpinned remainder, most-likely first.
   * Unknown ids are ignored and unlisted ones sort after, so a stale ranking
   * degrades to catalog order rather than hiding anything.
   */
  preferredIds: z.array(z.string()).default([]),
});

/**
 * The calendars kind carries no framing at all: every calendar the account has
 * is offered, in the order the client's own list returns them. There is nothing
 * to scope or rank — the user reads their own calendar names faster than any
 * inference about which of them "look like real commitments", and a calendar
 * filtered out is one they never get the chance to mark busy.
 */
const calendarsPickerTarget = z.object({
  kind: z.literal("calendars"),
});

/**
 * Recurring meetings are protected by default — the scheduler will not offer
 * that time to anyone. This card is where the user says which ones could
 * actually move. Every series is offered, in the order the client's own query
 * returns them; deciding which "look movable" on the user's behalf hides
 * options they would have picked.
 */
const protectedMeetingsPickerTarget = z.object({
  kind: z.literal("protectedMeetings"),
});

/**
 * The locations kind carries no framing either: there are exactly two addresses
 * to offer, home and work, and both are always shown. Which of them the user
 * fills in is the answer the card is asking for, so narrowing to one beforehand
 * would be answering it for them.
 */
const locationsPickerTarget = z.object({
  kind: z.literal("locations"),
});

/**
 * Routines are already running before the user arrives; this card is where they
 * see the whole set and turn things on or off. Every routine is offered,
 * enabled or not — one filtered out is one the user never learns exists.
 */
const routinesPickerTarget = z.object({
  kind: z.literal("routines"),
});

/**
 * The channels someone is in, to add okthx to after connecting Slack from a
 * chat card. Mirrors `slackChannelsPickerTarget` in `ent/schema/artifacts.ts`.
 * Names the workspace, since an account can connect more than one.
 */
const slackChannelsPickerTarget = z.object({
  kind: z.literal("slackChannels"),
  teamId: z.string(),
});

/**
 * One card the user picks things on, discriminated by what they're picking.
 *
 * Deliberately one component rather than one per kind: the visual body of each
 * kind is bespoke and hand-written twice (web and native), but the plumbing
 * around it — schema, view module, action route, registry entries on both
 * platforms — is identical, and duplicating that per kind is how the set stops
 * being maintainable. Adding a kind is a new `target` member plus a body in each
 * renderer; nothing else moves.
 *
 * **Framing, not data.** Like `conflictCheck`,
 * the renderer queries live state itself. Baking the list in would leave the
 * card contradicting itself the moment one row changes — a calendar gets
 * connected, a routine gets toggled somewhere else — and a card that argues with
 * the app is worse than one that takes a moment to load.
 */
export const pickerComponent = baseComponent.extend({
  type: z.literal("picker"),
  /** What's being picked, plus the framing that kind's renderer needs. */
  target: z.discriminatedUnion("kind", [
    connectorsPickerTarget,
    calendarsPickerTarget,
    protectedMeetingsPickerTarget,
    locationsPickerTarget,
    routinesPickerTarget,
    slackChannelsPickerTarget,
  ]),
});

/**
 * A masked field for a credential that goes straight to the account's computer.
 *
 * The typed value posts to `submitPath` and is never sent back to the thread,
 * so unlike every other input on a card there is no `value` here to prefill or
 * echo — the component carries only where to send it and what to ask.
 */
export const secretInputComponent = baseComponent.extend({
  type: z.literal("secretInput"),
  /** The agent's question, shown as the field's label. */
  question: z.string(),
  /**
   * The variable and host, as one line. Native builds from before `hint`
   * render their copy from this alone, so it is still sent.
   */
  path: z.string().optional(),
  /** The line under the field saying where the value goes. */
  hint: z.string(),
  /** What the card says once saved. */
  savedText: z.string(),
  /** The footer button that saves the value. */
  submitLabel: z.string(),
  /**
   * The one host the key will be sent to. Rendered on its own line, apart
   * from the agent-written question, because it is the only thing standing
   * between a key and a host the agent chose — a question can say anything.
   */
  host: z.string(),
  /**
   * Server route the value posts to. Never carries the value itself.
   *
   * Absent where the card is rendered outside a thread — a notification email,
   * say — because the route is thread-scoped and there is nowhere to send it.
   * The field renders read-only rather than offering a button that cannot work.
   */
  submitPath: z.string().optional(),
  /** Once saved or cancelled, the field is gone and only this remains. */
  state: z.enum(["waiting", "saving", "saved", "cancelled"]),
});

/**
 * A button that opens the account's computer.
 *
 * Carries no URL — deliberately, and this is the same absence `secretInput`
 * makes for the credential. The way in is signed and short-lived, so it is
 * fetched from `openPath` when the user clicks and discarded afterwards; one
 * embedded here would be stale in every rendering of the card that outlived its
 * expiry, including the copy sitting in an email.
 */
export const computerDesktopComponent = baseComponent.extend({
  type: z.literal("computerDesktop"),
  /**
   * Server route that mints the link. Absent outside a thread — a notification
   * email — where the route is unbuildable, and the card then renders as prose
   * rather than a button that cannot work.
   */
  openPath: z.string().optional(),
  /**
   * Server route that revokes a link `openPath` minted, called when the user
   * closes the desktop. Absent wherever `openPath` is, and on servers that
   * predate it — a client then closes without revoking, as before.
   */
  closePath: z.string().optional(),
  /**
   * Server route that types the user's clipboard on the computer, called from
   * the desktop's Paste button. Absent wherever `openPath` is, and on servers
   * that predate it — a client then shows no Paste button.
   */
  pastePath: z.string().optional(),
});

/** Clickable button row with optional icon. `openIn: "external"` opens in a
 * new tab, `"download"` is the same target hint for downloadable assets. */
export const linkButtonComponent = baseComponent.extend({
  type: z.literal("linkButton"),
  /** External / downloadable target. Set this OR `appLink`. */
  href: z.string().optional(),
  /** In-app route (wouter navigation), e.g. `/threads/:id`. When set, the button
   *  does SPA navigation instead of an `href` full-page/new-tab load. */
  appLink: z.string().optional(),
  label: z.string(),
  icon: headerIcon.optional(),
  openIn: z.enum(["external", "download"]).default("external"),
  /** When true, the href needs the user's session — the renderer omits the
   *  button on shared (unauthenticated) threads where it would 401. */
  requiresAuth: z.boolean().optional(),
  /** Native resolves this authenticated POST before opening a hosted payment action. */
  nativeLaunchPath: z.string().optional(),
  requiresShoppingPayments: z.boolean().optional(),
});

/** Live scheduling-conflict check for an inbound invitation. The renderer runs
 * the conflict query (`/a/events/conflicts`) for the event window and lists any
 * overlapping events — so the card shows *what* it conflicts with, not just
 * that it conflicts. `conflicts` may be pre-filled (server-provided or for the
 * preview); when present the renderer skips the live query. */
export const conflictCheckComponent = baseComponent.extend({
  type: z.literal("conflictCheck"),
  startMs: z.number().optional(),
  endMs: z.number().optional(),
  isAllDay: z.boolean().optional(),
  /** Excluded from the conflict scan so the invitation doesn't match itself. */
  externalEventId: z.string().optional(),
  /** Account's effective IANA zone — the conflict scan evaluates the event
   *  window in this zone (consistent with date rendering), not the device
   *  locale. Falls back to the device zone when omitted. */
  timeZone: z.string().optional(),
  conflicts: z
    .array(
      z.object({
        summary: z.string(),
        formattedRange: z.string(),
        location: z.string().optional(),
      }),
    )
    .optional(),
});

/** Compact email row: mail icon + linked subject (opens externally) + muted
 * "from <sender>". Used for the email-change affected-email list. */
export const emailLinkComponent = baseComponent.extend({
  type: z.literal("emailLink"),
  subject: z.string(),
  sender: z.string().optional(),
  href: z.string().optional(),
});

/** Conversation-style message-bubble list. Used for call transcripts and
 * any future chat-style replay. `role` drives the bubble side + color;
 * `timestampSeconds` is offset from the start of the conversation. */
export const transcriptComponent = baseComponent.extend({
  type: z.literal("transcript"),
  messages: z.array(
    z.object({
      role: z.enum(["user", "agent", "other"]),
      text: z.string(),
      timestampSeconds: z.number().optional(),
      speakerLabel: z.string().optional(),
    }),
  ),
});

/** Small inline tag — no icon, no severity. Use for "labels" lists or
 * lightweight chips. */
export const badgeComponent = baseComponent.extend({
  type: z.literal("badge"),
  label: z.string(),
  variant: z.enum(["default", "secondary"]).default("default"),
});

/** Avatar + name header with an optional inline status badge (icon + colored
 * text, not a pill). Used for "who did X" headers — e.g. an RSVP responder. */
export const personHeaderComponent = baseComponent.extend({
  type: z.literal("personHeader"),
  participant: viewParticipant,
  /** Optional muted sub-line under the name (e.g. a phone number or email). */
  subtitle: z.string().optional(),
  badge: z
    .object({
      label: z.string(),
      icon: headerIcon.optional(),
      severity: z.enum(["info", "success", "warning", "error", "muted"]).default("info"),
    })
    .optional(),
});

/** Sandboxed iframe rendering an HTML (or plain-text) body. Used for
 * external/untrusted email bodies — the renderer DOMPurify-sanitizes the
 * content, mounts it in an `<iframe sandbox>` with the email-formatting CSS
 * the legacy CommunicationBody used, and auto-sizes the iframe to its
 * content. The wire shape carries the raw body; the renderer handles
 * HTML/plain-text detection and quote-block grouping. */
export const sandboxedHtmlComponent = baseComponent.extend({
  type: z.literal("sandboxedHtml"),
  body: z.string(),
  /** Optional minimum iframe height in px. Defaults to 200 in the renderer. */
  minHeightPx: z.number().optional(),
});

/** Applet preview — a sandboxed, cross-origin iframe served by the API's
 * `/appletPreview` endpoint. The frame's content (and CSP isolation) is owned
 * by the server; the renderer only needs the artifact coordinates (read from
 * the view context) and an optional title for the iframe's accessible name. */
export const appletPreviewComponent = baseComponent.extend({
  type: z.literal("appletPreview"),
  title: z.string().nullable().optional(),
  /** Applet artifact URN, threaded to the client so a runtime-error "fix it"
   * message can tell the agent exactly which applet to reload and revise.
   * Optional + additive so an older client safely ignores it. */
  urn: z.string().optional(),
});

/** Document export actions — "Download PDF" + "Save to Google Drive" for a
 * document artifact. The buttons carry bespoke client behavior (a REST export
 * call and an agent-message send), so the renderer reads the artifact
 * coordinates from the view context rather than the component carrying data.
 * Hidden client-side on shared threads (the endpoints require auth). */
export const documentExportComponent = baseComponent.extend({
  type: z.literal("documentExport"),
});

/**
 * The kickoff "Schedule a meeting on Tuesday for 30 min at Zoom" composite.
 * Bespoke layout (popovers nested in prose + modal calendar picker) — kept
 * as one component so visual + interaction parity stays exact. Each field
 * carries a path for blur-saves; the calendar-picker modal is mounted by
 * the renderer.
 */
export const schedulingMadlibsComponent = baseComponent.extend({
  type: z.literal("schedulingMadlibs"),
  /** Suppresses the location + title segments for responder kickoffs. */
  isResponder: z.boolean(),
  editable: z.boolean(),

  roughTimeRange: z.object({
    text: z.string(),
    startISO: z.string(),
    endISO: z.string(),
  }),
  roughTimeRangeEditPath: z.string(),

  duration: z.number(),
  durationEditPath: z.string(),

  location: z.string(),
  locationEditPath: z.string(),
  virtualMeetingType: virtualMeetingType.optional(),
  virtualMeetingTypeEditPath: z.string(),

  subject: z.string(),
  subjectEditPath: z.string(),

  /** Surface an "N selected slots" link that opens the availability picker. */
  userConstraintRanges: z
    .object({
      ranges: z.array(z.object({ startISO: z.string(), endISO: z.string() })),
    })
    .nullable()
    .optional(),
  userConstraintRangesEditPath: z.string(),

  defaultAvailabilityRanges: z
    .array(z.object({ startISO: z.string(), endISO: z.string() }))
    .optional(),
  skipCalendarConflicts: z.boolean().optional(),
  skipCalendarConflictsEditPath: z.string(),
});

/**
 * Merged kickoff body + embedded slot block, in one card section.
 *
 * Server pre-splits the prose at the closing-line paragraph (via
 * `findSlotInsertionPoint`) so the card preview can render
 * `[bodyBefore (markdown)] [slot block] [bodyAfter (markdown + footer)]`
 * in-flow — the user sees the slot block at the same position the
 * recipient will see the bulleted times in the rendered email.
 *
 * Editing is unified: the `messageBody` field is the full prose source
 * (no bullets, no marker). The renderer toggles between read-only
 * preview (split with the slot block embedded) and a single edit
 * surface for the entire prose body. No split-editor save race because
 * only one editor is open at a time.
 */
export const kickoffBodyWithSlotsComponent = baseComponent.extend({
  type: z.literal("kickoffBodyWithSlots"),
  /** Full prose-only body the user edits. */
  messageBody: z.string(),
  /** Dot-path for the messageBody patch (typically "messageBody"). */
  messageBodyEditPath: z.string(),
  /** Prose to render BEFORE the embedded slot block. Preview-only. */
  bodyBefore: z.string(),
  /** Prose + signature + footer to render AFTER the embedded slot block. Preview-only. */
  bodyAfter: z.string(),
  /** Proposed slots — `{ startMsSinceEpoch, endMsSinceEpoch }` pairs. */
  slots: z.array(z.object({ startMsSinceEpoch: z.number(), endMsSinceEpoch: z.number() })),
  /** Dot-path for the slots patch (typically "proposedSlots"). */
  slotsEditPath: z.string(),
  /** Meeting duration in minutes — drives the slot picker's slot length. */
  duration: z.number(),
  /** Dot-path for the duration patch (typically "duration"). */
  durationEditPath: z.string(),
  /** Calendar's anchor date (ISO) — typically the kickoff's roughTimeRange start. */
  initialDateISO: z.string(),
  editable: z.boolean(),
});

/**
 * Instant Book time picker — a single day, one column per person, with the
 * meeting placed on the grid and draggable.
 *
 * Deliberately carries no calendar data — not the events, not the window, not
 * which day to open on. Those come from the picker's own endpoint, because the
 * user pages between days and because a kickoff can sit for hours before it's
 * opened, so anything baked in at build time would be stale exactly when the
 * user is deciding. What IS here is the placed slot, which must render before
 * any fetch resolves so the card can name the time it is about to book.
 */
export const instantBookPickerComponent = baseComponent.extend({
  type: z.literal("instantBookPicker"),
  /** The slot on the grid, or null when nothing is placed. */
  slot: z
    .object({
      startMsSinceEpoch: z.number(),
      endMsSinceEpoch: z.number(),
      source: z.enum(["recommended", "user"]),
    })
    .nullable(),
  /** Dot-path for the slot patch (typically "instantBookSlot"). */
  slotEditPath: z.string(),
  /** Meeting duration in minutes — the placed slot's length. */
  duration: z.number(),
  /** Dot-path for the duration patch (typically "duration"). */
  durationEditPath: z.string(),
  /** Dot-path for the Instant Book toggle, so the picker's "coordinate over
   * email instead" choice can switch the meeting to the proposal path. */
  autoScheduleEditPath: z.string(),
  /**
   * The zone the grid draws in — the meeting's, so a card opened from another
   * country still shows the hours it will actually happen at. Sent with the
   * view rather than with the calendar data so the client can lay out any day
   * before a single request returns.
   */
  timezone: z.string(),
  /**
   * Changes when the participants do. The grid is one column per person, and
   * the calendars behind it are fetched separately from this view — so the
   * client keys that fetch on this token and a participant added on the card
   * gets a column straight away, instead of whenever something else happened
   * to refetch the day.
   */
  participantsKey: z.string(),
  /**
   * `YYYY-MM-DD` — the first day of the meeting's window, in `timezone`. The
   * grid opens here when no time is placed: the user asked about that window,
   * so that is the day worth showing, and today may have nothing to do with
   * it. Absent when the window can't be read as a date. The client still
   * clamps to today, which is as far back as paging goes.
   */
  windowStartDate: z.string().optional(),
  /** No time in the window works for everyone: render the empty state and the
   * choice between email and booking over a conflict. */
  noMutualTime: z.boolean(),
  editable: z.boolean(),
});

/** Single-choice dropdown. Stores the option value as a string at `editPath`
 * — the server applier is responsible for coercing back to its enum/literal
 * type. `helpText` renders as an info tooltip next to the label. */
export const selectComponent = baseComponent.extend({
  type: z.literal("select"),
  editPath: z.string(),
  label: z.string().optional(),
  helpText: z.string().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() })),
  value: z.string(),
  editable: z.boolean(),
  /** `"inline"` (default) => compact label + dropdown + info tooltip. `"card"`
   *  => full-width bordered row with the help text inline beneath the label and
   *  the dropdown on the right (matches the permission-toggle cards). */
  style: z.enum(["inline", "card"]).optional(),
});

/** Numeric input. `value` is `null` when empty; the server applier decides
 * whether to coerce that to 0, leave the field unchanged, or reject. When
 * `required` is set, clearing the input writes `min ?? 0` instead of `null`
 * so the field can't be left blank (mirrors the legacy `v ?? 0` coercion). */
export const numberInputComponent = baseComponent.extend({
  type: z.literal("numberInput"),
  editPath: z.string(),
  label: z.string().optional(),
  value: z.number().nullable(),
  required: z.boolean().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
  /** Right-aligned unit text inside the input row (e.g. "minutes"). */
  suffix: z.string().optional(),
  placeholder: z.string().optional(),
  editable: z.boolean(),
});

/** Cron schedule picker — frequency + day-of-week + time-of-day with a custom
 * cron-string escape hatch. Single irreducible composite: the picker UI is
 * tightly coupled (changing frequency changes which sub-pickers render). */
export const cronScheduleComponent = baseComponent.extend({
  type: z.literal("cronSchedule"),
  editPath: z.string(),
  value: z.string(),
  /** Optional IANA timezone label rendered next to the schedule. */
  timezone: z.string().optional(),
  editable: z.boolean(),
});

/**
 * Leaf components — every component type that doesn't itself contain
 * children. `horizontalGroup` accepts an array of these, which keeps the
 * union closed (no nested groups) and the Zod schema strictly tree-
 * structured. If we want column-of-rows layouts later, lift the constraint
 * by replacing `z.array(leafComponent)` with `z.array(artifactComponent)`
 * and accepting the `z.lazy()` plumbing.
 */
/**
 * Composite email recipients section — encodes the legacy EmailDraft layout
 * where `from` and `to/cc/bcc` share a single Edit button: collapsed view
 * shows "From: pill" + a combined "Recipients:" pill row; editing expands
 * into separate To/Cc/Bcc lists with add/remove. Stays as a domain composite
 * because the to/cc/bcc lists need to read/write three independent paths
 * under one editor — that's not expressible as a generic group of
 * `participantList` components without coordinating editing state across
 * them.
 */
export const emailRecipientsComponent = baseComponent.extend({
  type: z.literal("emailRecipients"),
  from: viewParticipant,
  to: z.array(viewParticipant),
  cc: z.array(viewParticipant).optional(),
  bcc: z.array(viewParticipant).optional(),
  /**
   * When set, the From pill is clickable in edit mode and opens a picker
   * that lets the user toggle the sender between themselves and their
   * assistant. The path receives the chosen handle id as a string.
   */
  fromEditPath: z.string().optional(),
  toEditPath: z.string(),
  ccEditPath: z.string(),
  bccEditPath: z.string(),
  editable: z.boolean(),
});

/** A single attachment row inside an `emailAttachments` component. */
export const emailAttachmentView = z.object({
  // Stable handle the client passes back to DELETE the attachment. For uploaded
  // files this is the GCS fileId.
  id: z.string(),
  filename: z.string(),
  contentType: z.string(),
  size: z.number(),
  /** Session-authed path that downloads the file; the chip opens it when set. */
  href: z.string().optional(),
});
export type EmailAttachmentView = z.infer<typeof emailAttachmentView>;

/**
 * Attachment list for an email: a draft's, or a received email's files. Renders
 * chips with a remove button when `editable`; the block also surfaces the upload
 * affordance (paperclip + drop-zone) in edit mode. Mutates through the dedicated
 * `.../artifacts/:id/attachments` endpoint, not the generic patch path.
 */
export const emailAttachmentsComponent = baseComponent.extend({
  type: z.literal("emailAttachments"),
  attachments: z.array(emailAttachmentView),
  editable: z.boolean(),
});

const leafComponent = z.discriminatedUnion("type", [
  textComponent,
  markdownComponent,
  participantListComponent,
  emailRecipientsComponent,
  emailAttachmentsComponent,
  dateTimeRangeComponent,
  recurrenceComponent,
  remindersComponent,
  locationComponent,
  virtualMeetingComponent,
  infoBannerComponent,
  toggleComponent,
  schedulingMadlibsComponent,
  kickoffBodyWithSlotsComponent,
  instantBookPickerComponent,
  selectComponent,
  numberInputComponent,
  cronScheduleComponent,
  sectionLabelComponent,
  dividerComponent,
  statusBadgeComponent,
  fieldRowComponent,
  diffComponent,
  imagePreviewComponent,
  audioPlayerComponent,
  linkButtonComponent,
  transcriptComponent,
  badgeComponent,
  personHeaderComponent,
  connectorHeaderComponent,
  pickerComponent,
  emailLinkComponent,
  conflictCheckComponent,
  sandboxedHtmlComponent,
  appletPreviewComponent,
  documentExportComponent,
]);
export type LeafComponent = z.infer<typeof leafComponent>;

/**
 * Horizontal layout container. Children flow horizontally with configurable
 * gap, alignment, and justify. `responsive: "wrap"` lets children wrap to a
 * new line on overflow; `"stack"` switches to a vertical column on small
 * containers (uses Tailwind container queries — `@md` and up stay
 * horizontal). No recursion: children must be leaf components.
 */
export const horizontalGroupComponent = baseComponent.extend({
  type: z.literal("horizontalGroup"),
  align: z.enum(["start", "center", "end"]).default("start"),
  justify: z.enum(["start", "between", "end"]).default("start"),
  gap: z.enum(["1", "2", "3", "4"]).default("2"),
  responsive: z.enum(["wrap", "stack"]).default("wrap"),
  children: z.array(leafComponent),
});

/**
 * Boxed vertical group — renders its children stacked inside a subtle
 * bordered card with an optional leading icon + title. Used to visually group
 * related rows (e.g. the meeting-info block on an RSVP card). Children are
 * leaf components (no nested containers), mirroring `horizontalGroup`.
 */
export const cardComponent = baseComponent.extend({
  type: z.literal("card"),
  shopping: shoppingCardSpec.optional(),
  icon: headerIcon.optional(),
  title: z.string().optional(),
  children: z.array(leafComponent),
});

export const artifactComponent = z.discriminatedUnion("type", [
  textComponent,
  markdownComponent,
  participantListComponent,
  emailRecipientsComponent,
  emailAttachmentsComponent,
  dateTimeRangeComponent,
  recurrenceComponent,
  remindersComponent,
  locationComponent,
  virtualMeetingComponent,
  infoBannerComponent,
  toggleComponent,
  schedulingMadlibsComponent,
  kickoffBodyWithSlotsComponent,
  instantBookPickerComponent,
  selectComponent,
  numberInputComponent,
  cronScheduleComponent,
  sectionLabelComponent,
  dividerComponent,
  horizontalGroupComponent,
  cardComponent,
  statusBadgeComponent,
  fieldRowComponent,
  diffComponent,
  imagePreviewComponent,
  audioPlayerComponent,
  linkButtonComponent,
  transcriptComponent,
  badgeComponent,
  personHeaderComponent,
  connectorHeaderComponent,
  pickerComponent,
  secretInputComponent,
  computerDesktopComponent,
  emailLinkComponent,
  conflictCheckComponent,
  sandboxedHtmlComponent,
  appletPreviewComponent,
  documentExportComponent,
]);
export type ArtifactComponent = z.infer<typeof artifactComponent>;

export const artifactView = z.object({
  version: z.literal(1),
  components: z.array(artifactComponent),
  /** Vertical rhythm between top-level components. `"normal"` (default) is the
   *  roomy `gap-4` used by display cards; `"compact"` tightens it for dense
   *  form-style cards like the routine editor. */
  spacing: z.enum(["normal", "compact"]).optional(),
});
export type ArtifactView = z.infer<typeof artifactView>;

/**
 * Wire schema for the generic `POST /a/threads/.../artifacts/:id/patch`
 * endpoint. A patch is `{ path, value }`; the handler enforces a per-type
 * writable-path allowlist before forwarding to the per-type applier.
 */
export const fieldPatchSchema = z.object({
  path: z.string().min(1),
  value: z.unknown(),
});
export type FieldPatch = z.infer<typeof fieldPatchSchema>;

export const artifactPatchBody = z.object({
  patches: z.array(fieldPatchSchema).min(1),
});
export type ArtifactPatchBody = z.infer<typeof artifactPatchBody>;
